import { useEffect, useRef, useState, type ReactNode } from "react";
import "./PinchZoom.css";

interface PinchZoomProps {
  children: ReactNode;
  /** Min/max scale clamps. */
  min?: number;
  max?: number;
  className?: string;
}

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const RESET: Transform = { scale: 1, x: 0, y: 0 };

/* Lightweight pinch-zoom + pan for the mobile map.
 *
 * Pinch (2 fingers) scales around the midpoint between the fingers.
 * Single-finger drag pans only when zoomed in (scale > 1) so a tap on a pin
 * still reaches the SVG below us. Double-tap resets.
 *
 * The transform is applied via CSS on a wrapper around the children, which
 * keeps the SVG's vector rendering crisp at every zoom level. */
export function PinchZoom({ children, min = 1, max = 4, className }: PinchZoomProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState<Transform>(RESET);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let mode: "idle" | "pinch" | "pan" = "idle";
    let startDist = 0;
    let startScale = 1;
    let startMidContent = { x: 0, y: 0 };
    let startMidViewport = { x: 0, y: 0 };
    let startT: Transform = RESET;
    let panStart = { x: 0, y: 0 };
    let lastTap = 0;

    const onTouchStart = (e: TouchEvent) => {
      const rect = el.getBoundingClientRect();
      if (e.touches.length === 2) {
        e.preventDefault();
        const a = e.touches[0];
        const b = e.touches[1];
        startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
        startScale = t.scale;
        startT = { ...t };
        startMidViewport = {
          x: (a.clientX + b.clientX) / 2 - rect.left,
          y: (a.clientY + b.clientY) / 2 - rect.top,
        };
        // Pinch midpoint expressed in *content* coordinates (pre-transform).
        startMidContent = {
          x: (startMidViewport.x - t.x) / t.scale,
          y: (startMidViewport.y - t.y) / t.scale,
        };
        mode = "pinch";
      } else if (e.touches.length === 1) {
        const now = Date.now();
        if (now - lastTap < 300) {
          // Double-tap reset.
          setT(RESET);
          mode = "idle";
          lastTap = 0;
          return;
        }
        lastTap = now;
        if (t.scale > 1.001) {
          panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          startT = { ...t };
          mode = "pan";
        } else {
          mode = "idle";
        }
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (mode === "pinch" && e.touches.length === 2) {
        e.preventDefault();
        const a = e.touches[0];
        const b = e.touches[1];
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
        const next = Math.max(min, Math.min(max, startScale * (dist / startDist)));
        // Keep the pinch midpoint anchored to its content point.
        const x = startMidViewport.x - startMidContent.x * next;
        const y = startMidViewport.y - startMidContent.y * next;
        setT({ scale: next, x, y });
      } else if (mode === "pan" && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - panStart.x;
        const dy = e.touches[0].clientY - panStart.y;
        setT({ scale: startT.scale, x: startT.x + dx, y: startT.y + dy });
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        mode = "idle";
      } else if (e.touches.length === 1 && mode === "pinch") {
        // Pinch released, switch to single-finger pan if still zoomed in.
        if (t.scale > 1.001) {
          panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          startT = { ...t };
          mode = "pan";
        } else {
          mode = "idle";
        }
      }
    };

    // passive:false so we can preventDefault during pinch
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [t, min, max]);

  const zoomed = t.scale > 1.001;

  return (
    <div ref={containerRef} className={`PinchZoom${className ? " " + className : ""}`}>
      <div
        className="PinchZoom__inner"
        style={{
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
      {zoomed && (
        <button
          type="button"
          className="PinchZoom__reset"
          onClick={() => setT(RESET)}
          aria-label="Reset zoom"
        >
          Reset zoom
        </button>
      )}
    </div>
  );
}
